// Pure helpers for the strength app: formatting, workout assembly and Strava redirect
// parsing. Kept free of React so they can be unit tested directly.

export const DEFAULT_REST_SEC = 90;

export function formatNumber(value, lang, maxDecimals = 2) {
  if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) return '';
  return new Intl.NumberFormat(lang === 'no' ? 'nb-NO' : 'en-GB', { maximumFractionDigits: maxDecimals }).format(Number(value));
}

export const formatKg = (kg, lang) => `${formatNumber(kg, lang)} kg`;

export function formatDuration(totalSeconds) {
  const seconds = Math.max(0, Math.round(Number(totalSeconds) || 0));
  if (seconds < 60) return `${seconds} s`;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = String(seconds % 60).padStart(2, '0');
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${rest}` : `${minutes}:${rest}`;
}

export function formatSet(set, lang) {
  const parts = [];
  if (set.durationSec) parts.push(formatDuration(set.durationSec));
  if (set.reps) parts.push(set.weightKg ? `${set.reps} × ${formatKg(set.weightKg, lang)}` : `${set.reps} reps`);
  else if (set.weightKg) parts.push(formatKg(set.weightKg, lang));
  return parts.join(' · ');
}

export function formatTarget(target, lang) {
  if (!target) return '';
  const sets = target.targetSets || 1;
  let core;
  if (target.targetDurationSec) {
    core = formatDuration(target.targetDurationSec);
  } else {
    const min = target.targetRepsMin;
    const max = target.targetRepsMax;
    core = min && max && min !== max ? `${min}–${max}` : `${min || max || '?'}`;
  }
  const load = target.targetWeightKg ? ` @ ${formatKg(target.targetWeightKg, lang)}` : '';
  return `${sets} × ${core}${load}`;
}

// The API returns UTC timestamps; values read back from SQL Server lack the "Z".
export function parseApiDate(value) {
  if (!value) return null;
  return new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`);
}

// Progress dates are already local calendar days; never shift them through a time zone.
export function parseLocalDay(value) {
  if (!value) return null;
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function formatDate(value, lang, options = { weekday: 'short', day: 'numeric', month: 'short' }) {
  const date = value instanceof Date ? value : parseApiDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat(lang === 'no' ? 'nb-NO' : 'en-GB', options).format(date);
}

export function elapsedSince(startValue, now = Date.now()) {
  const start = parseApiDate(startValue);
  const seconds = start ? Math.max(0, Math.floor((now - start.getTime()) / 1000)) : 0;
  // A stopwatch reads 0:04, not "4 s".
  return seconds < 60 ? `0:${String(seconds).padStart(2, '0')}` : formatDuration(seconds);
}

/**
 * The exercises to show while training: the plan day in plan order, then anything logged
 * that is not in the plan, then exercises added on the fly but not yet logged.
 */
export function buildWorkoutExercises(planDay, sets, extraKeys, catalog) {
  const list = [];
  const seen = new Set();

  (planDay?.exercises || []).forEach((exercise, index) => {
    if (seen.has(exercise.exerciseKey)) return;
    seen.add(exercise.exerciseKey);
    list.push({ key: exercise.exerciseKey, name: exercise.exerciseName, kind: exercise.kind, target: exercise, order: index });
  });

  const addExtra = (key, name, order) => {
    if (seen.has(key)) return;
    seen.add(key);
    const known = catalog?.[key];
    list.push({ key, name: known?.name || name || key, kind: known?.kind || 'Weighted', target: null, order });
  };

  [...(sets || [])]
    .sort((a, b) => a.exerciseOrder - b.exerciseOrder || a.setNumber - b.setNumber)
    .forEach(set => addExtra(set.exerciseKey, set.exerciseName, set.exerciseOrder));
  (extraKeys || []).forEach(key => addExtra(key, null, null));

  let next = list.reduce((max, e) => (e.order === null ? max : Math.max(max, e.order)), -1) + 1;
  list.forEach(e => { if (e.order === null) e.order = next++; });
  return list.sort((a, b) => a.order - b.order);
}

export const setsFor = (sets, key) =>
  (sets || []).filter(s => s.exerciseKey === key).sort((a, b) => a.setNumber - b.setNumber);

export const workingSets = (sets) => (sets || []).filter(s => !s.isWarmup);

// Timed exercises only carry a load when it is held, e.g. a farmer's walk; a plank does not.
const HELD_LOADS = ['Barbell', 'Dumbbell', 'Kettlebell'];
export const takesLoad = (exercise) =>
  exercise?.kind === 'Weighted' ||
  (exercise?.kind === 'Timed' && (exercise.equipment || []).some(e => HELD_LOADS.includes(e)));

/**
 * What to put in the input row. Once a working set is logged, repeat it: the next set is
 * usually the same. Before that, use the progression suggestion, then the plan target.
 */
export function prefillFor(exercise, guidance, loggedSets) {
  const lastWorking = workingSets(loggedSets).slice(-1)[0];
  if (lastWorking) {
    return {
      weightKg: lastWorking.weightKg ?? '',
      reps: lastWorking.reps ?? '',
      durationSec: lastWorking.durationSec ?? ''
    };
  }
  const target = exercise.target;
  return {
    weightKg: guidance?.nextWeightKg ?? target?.targetWeightKg ?? '',
    reps: guidance?.nextReps ?? target?.targetRepsMin ?? target?.targetRepsMax ?? '',
    durationSec: guidance?.nextDurationSec ?? target?.targetDurationSec ?? ''
  };
}

// Turns a raw input row into the API's set shape, or null when nothing was measured.
export function toSetRequest(exercise, values, isWarmup) {
  const num = (v) => (v === '' || v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v));
  const reps = exercise.kind === 'Timed' ? null : num(values.reps);
  const weightKg = exercise.kind === 'Bodyweight' && !num(values.weightKg) ? null : num(values.weightKg);
  const durationSec = exercise.kind === 'Timed' ? num(values.durationSec) : null;
  if (!(reps > 0) && !(durationSec > 0) && !(weightKg > 0)) return null;
  return {
    exerciseKey: exercise.key,
    exerciseOrder: exercise.order,
    reps: reps > 0 ? Math.round(reps) : null,
    weightKg: weightKg > 0 ? weightKg : null,
    durationSec: durationSec > 0 ? Math.round(durationSec) : null,
    isWarmup: !!isWarmup
  };
}

export function stepValue(value, step, direction, min = 0) {
  const current = Number(value) || 0;
  const next = Math.round((current + step * direction) * 100) / 100;
  return Math.max(min, next);
}

export function errorText(error, fallback) {
  const message = error?.message || '';
  if (message.startsWith('{')) {
    try {
      const parsed = JSON.parse(message);
      return parsed.detail || parsed.title || fallback;
    } catch {
      return fallback;
    }
  }
  return message && message.length < 300 && !message.startsWith('<') ? message : fallback;
}

export const stravaActivityUrl = (id) => `https://www.strava.com/activities/${id}`;

// Strava sends the athlete back with ?code&state&scope, or ?error=access_denied.
export function readStravaReturn(search) {
  const params = new URLSearchParams(search || '');
  if (params.get('error') && params.get('state')) return { error: params.get('error') };
  const code = params.get('code');
  const state = params.get('state');
  if (code && state) return { code, state, scope: params.get('scope') || '' };
  return null;
}

export const PLAN_GOALS = ['Strength', 'Hypertrophy', 'GeneralFitness', 'Endurance'];

// How a workout felt, 1 (rough) to 5 (great). Context for the coach, not a score.
export const FEELINGS = [
  { value: 1, emoji: '😫' },
  { value: 2, emoji: '😕' },
  { value: 3, emoji: '😐' },
  { value: 4, emoji: '🙂' },
  { value: 5, emoji: '💪' }
];
export const feelingEmoji = (value) => FEELINGS.find(f => f.value === value)?.emoji || '';
export const EXPERIENCE_LEVELS = ['Beginner', 'Intermediate', 'Advanced'];
export const EQUIPMENT = ['Barbell', 'Dumbbell', 'Kettlebell', 'Bench', 'PullUpBar', 'Cable', 'Machine', 'Band'];
export const MUSCLE_GROUPS = ['Chest', 'Back', 'Shoulders', 'Legs', 'Glutes', 'Arms', 'Core', 'FullBody', 'Mobility'];

// An editable plan request built from a saved plan (or an AI draft, which already is one).
export function toPlanRequest(plan) {
  return {
    name: plan.name || '',
    description: plan.description || '',
    goal: plan.goal || 'GeneralFitness',
    daysPerWeek: plan.daysPerWeek || 3,
    isAiGenerated: !!plan.isAiGenerated,
    aiRationale: plan.aiRationale || null,
    days: (plan.days || []).map(day => ({
      id: day.id || null,
      name: day.name,
      notes: day.notes || '',
      exercises: (day.exercises || []).map(e => ({
        exerciseKey: e.exerciseKey,
        targetSets: e.targetSets,
        targetRepsMin: e.targetRepsMin ?? null,
        targetRepsMax: e.targetRepsMax ?? null,
        targetDurationSec: e.targetDurationSec ?? null,
        targetWeightKg: e.targetWeightKg ?? null,
        restSec: e.restSec ?? null,
        notes: e.notes || ''
      }))
    }))
  };
}

export function newPlanExercise(exercise) {
  const timed = exercise.kind === 'Timed';
  return {
    exerciseKey: exercise.key,
    targetSets: 3,
    targetRepsMin: timed ? null : 8,
    targetRepsMax: timed ? null : 12,
    targetDurationSec: timed ? 30 : null,
    targetWeightKg: null,
    restSec: DEFAULT_REST_SEC,
    notes: ''
  };
}
